import '@theme/dialog';

const triggerSelector = '[data-orska-video-open]';
const modalSelector = 'dialog-component.orska-video-modal';
const toggleSelector = '[data-orska-video-toggle]';
const initializedModals = new WeakSet();

document.addEventListener('click', (event) => {
  const target = event.target;

  if (!(target instanceof Element)) return;

  const trigger = target.closest(triggerSelector);

  if (!(trigger instanceof HTMLElement)) return;

  const modalId = trigger.getAttribute('data-orska-video-open');
  const modal = modalId ? document.getElementById(modalId) : null;
  const dialog = modal?.querySelector('dialog');

  if (!(modal instanceof HTMLElement) || !(dialog instanceof HTMLDialogElement)) return;

  initializeModal(modal);

  const canOpen = loadExternalVideo(modal) || Boolean(modal.querySelector('video, iframe'));

  if (!canOpen) return;

  event.preventDefault();

  if (typeof modal.showDialog === 'function') {
    modal.showDialog();
  } else {
    dialog.showModal();
  }

  playModalMedia(modal);
});

function initializeModal(modal) {
  if (initializedModals.has(modal)) return;

  initializedModals.add(modal);
  modal.addEventListener('dialog:close', () => resetModalMedia(modal));
  modal.querySelector(toggleSelector)?.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    toggleModalMedia(modal);
  });

  const dialog = modal.querySelector('dialog');
  dialog?.addEventListener('close', () => resetModalMedia(modal));
  modal.querySelectorAll('video').forEach((video) => setupNativeVideoListeners(modal, video));
  setModalPlaybackState(modal, false);
}

function initializeModals(root = document) {
  root.querySelectorAll(modalSelector).forEach((modal) => {
    if (modal instanceof HTMLElement) initializeModal(modal);
  });
}

function loadExternalVideo(modal) {
  const frame = modal.querySelector('[data-orska-video-url]');

  if (!(frame instanceof HTMLElement)) return false;
  if (frame.hasAttribute('data-orska-video-loaded')) return true;

  const videoUrl = frame.dataset.orskaVideoUrl;
  const label = frame.dataset.orskaVideoLabel || 'Video';
  const embed = createEmbed(videoUrl, label);

  if (!embed) return false;

  frame.replaceChildren(embed);
  frame.setAttribute('data-orska-video-loaded', 'true');

  if (embed instanceof HTMLVideoElement) {
    setupNativeVideoListeners(modal, embed);
  }

  return true;
}

function createEmbed(videoUrl, label) {
  const url = toUrl(videoUrl);

  if (!url) return null;

  const youtubeId = getYouTubeId(url);

  if (youtubeId) {
    const src = new URL(`https://www.youtube.com/embed/${youtubeId}`);
    src.searchParams.set('autoplay', '1');
    src.searchParams.set('controls', '0');
    src.searchParams.set('disablekb', '1');
    src.searchParams.set('enablejsapi', '1');
    src.searchParams.set('modestbranding', '1');
    src.searchParams.set('playsinline', '1');
    src.searchParams.set('rel', '0');
    src.searchParams.set('origin', window.location.origin);

    return createIframe(src.href, label, 'youtube');
  }

  const vimeoId = getVimeoId(url);

  if (vimeoId) {
    const src = new URL(`https://player.vimeo.com/video/${vimeoId}`);
    src.searchParams.set('autoplay', '1');
    src.searchParams.set('background', '0');
    src.searchParams.set('byline', '0');
    src.searchParams.set('controls', '0');
    src.searchParams.set('portrait', '0');
    src.searchParams.set('title', '0');

    return createIframe(src.href, label, 'vimeo');
  }

  if (isDirectVideoUrl(url)) {
    const video = document.createElement('video');
    video.src = url.href;
    video.controls = false;
    video.autoplay = true;
    video.playsInline = true;

    return video;
  }

  if (!['http:', 'https:'].includes(url.protocol)) return null;

  return createIframe(url.href, label);
}

function createIframe(src, label, videoType = '') {
  const iframe = document.createElement('iframe');

  iframe.src = src;
  iframe.title = label;
  iframe.allow = 'autoplay; encrypted-media; picture-in-picture';
  iframe.allowFullscreen = true;

  if (videoType) {
    iframe.dataset.orskaVideoType = videoType;
  }

  return iframe;
}

function toggleModalMedia(modal) {
  if (modal.classList.contains('is-paused')) {
    playModalMedia(modal);
  } else {
    pauseModalMedia(modal);
  }
}

function playModalMedia(modal) {
  setModalPlaybackState(modal, true);

  window.setTimeout(() => {
    let hasNativeVideo = false;

    modal.querySelectorAll('video').forEach((video) => {
      hasNativeVideo = true;
      video.play().catch(() => setModalPlaybackState(modal, false));
    });

    modal.querySelectorAll('iframe[data-orska-video-type]').forEach((iframe) => playIframe(iframe));

    if (!hasNativeVideo && modal.querySelector('iframe[data-orska-video-type]')) {
      setModalPlaybackState(modal, true);
    }
  }, 0);
}

function pauseModalMedia(modal) {
  modal.querySelectorAll('video').forEach((video) => video.pause());
  modal.querySelectorAll('iframe[data-orska-video-type]').forEach((iframe) => pauseIframe(iframe));
  setModalPlaybackState(modal, false);
}

function resetModalMedia(modal) {
  modal.querySelectorAll('video').forEach((video) => {
    video.pause();

    try {
      video.currentTime = 0;
    } catch (error) {}
  });

  modal.querySelectorAll('[data-orska-video-url][data-orska-video-loaded]').forEach((frame) => {
    frame.replaceChildren();
    frame.removeAttribute('data-orska-video-loaded');
  });

  setModalPlaybackState(modal, false);
}

function setupNativeVideoListeners(modal, video) {
  if (video.dataset.orskaVideoListeners === 'true') return;

  video.dataset.orskaVideoListeners = 'true';
  video.addEventListener('play', () => setModalPlaybackState(modal, true));
  video.addEventListener('playing', () => setModalPlaybackState(modal, true));
  video.addEventListener('pause', () => setModalPlaybackState(modal, false));
  video.addEventListener('ended', () => setModalPlaybackState(modal, false));
}

function setModalPlaybackState(modal, isPlaying) {
  modal.classList.toggle('is-paused', !isPlaying);

  const button = modal.querySelector(toggleSelector);

  if (!(button instanceof HTMLButtonElement)) return;

  button.setAttribute(
    'aria-label',
    isPlaying ? button.dataset.orskaVideoPauseLabel || 'Pause video' : button.dataset.orskaVideoPlayLabel || 'Play video'
  );
}

function playIframe(iframe) {
  const type = iframe.dataset.orskaVideoType;

  if (type === 'youtube') {
    iframe.contentWindow?.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
  } else if (type === 'vimeo') {
    iframe.contentWindow?.postMessage('{"method":"play"}', '*');
  }
}

function pauseIframe(iframe) {
  const type = iframe.dataset.orskaVideoType;

  if (type === 'youtube') {
    iframe.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
  } else if (type === 'vimeo') {
    iframe.contentWindow?.postMessage('{"method":"pause"}', '*');
  }
}

function toUrl(value) {
  if (!value) return null;

  try {
    return new URL(value, window.location.origin);
  } catch (error) {
    return null;
  }
}

function getYouTubeId(url) {
  const hostname = url.hostname.replace(/^www\./, '');

  if (hostname === 'youtu.be') {
    return url.pathname.split('/').filter(Boolean)[0] || '';
  }

  if (!hostname.endsWith('youtube.com')) return '';

  const searchId = url.searchParams.get('v');

  if (searchId) return searchId;

  const [mode, id] = url.pathname.split('/').filter(Boolean);

  return ['embed', 'shorts', 'live'].includes(mode) ? id || '' : '';
}

function getVimeoId(url) {
  const hostname = url.hostname.replace(/^www\./, '');

  if (!hostname.endsWith('vimeo.com')) return '';

  const segments = url.pathname.split('/').filter(Boolean);

  if (segments[0] === 'video') return segments[1] || '';

  return segments.find((segment) => /^\d+$/.test(segment)) || '';
}

function isDirectVideoUrl(url) {
  return /\.(mp4|m4v|mov|webm)$/i.test(url.pathname);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initializeModals(), { once: true });
} else {
  initializeModals();
}

document.addEventListener('shopify:section:load', (event) => initializeModals(event.target));
